import type { i18n } from 'i18next'

const languages: Record<string, string> = { en: 'eng', pt: 'por', es: 'spa' }
// Whole technical quantities have no prose to translate; never match words or named parts.
const TECHNICAL_QUANTITY =
  /^(?:[\s\d.,%()+−×/=:;–—-]|(?:min|h|s|mm|cm|m|kg|g|ml|mL|L|bar|psi|rpm|kW|W|Hz|V|A|Nm|°C|°F)(?![\p{L}\p{N}_]))+$/u
/** Detection is an optimization only: short, uncertain or mixed text must still translate. */
export async function alreadyInDisplayLanguage(text: string, locale: string): Promise<boolean> {
  const target = languages[locale.split('-')[0]]
  if (target && /\d/.test(text) && TECHNICAL_QUANTITY.test(text)) return true
  const { francAll } = await import('franc-min')
  const parts = text.split(/\n+|(?<=[.!?])\s+/).filter((part) => part.trim())
  return Boolean(
    target &&
    parts.length &&
    parts.every((part) => {
      if ((part.match(/\p{L}+/gu)?.length ?? 0) < 4 || (part.match(/\p{L}/gu)?.length ?? 0) < 25)
        return false
      const scores = francAll(part, { only: ['eng', 'por', 'spa'], minLength: 20 })
      return scores[0]?.[0] === target && scores[0][1] - (scores[1]?.[1] ?? 1) >= 0.2
    }),
  )
}

const catalogs = new WeakMap<i18n, Map<string, Map<string, string>>>()
/**
 * `useTranslation().i18n` (react-i18next 16) devolve uma CÓPIA por componente, com `__original`
 * apontando para a instância real. Chavear o cache na cópia reconstruía o catálogo de 3 idiomas
 * a cada detalhe de lista (~90 ms por linha — QA R104). A chave é sempre a instância real.
 */
const watched = new WeakSet<i18n>()
function rootInstance(instance: i18n): i18n {
  return (instance as i18n & { __original?: i18n }).__original ?? instance
}
function entries(value: unknown, prefix = ''): Array<[string, string]> {
  if (!value || typeof value !== 'object') return []
  return Object.entries(value).flatMap(([key, child]) => {
    const path = prefix ? `${prefix}.${key}` : key
    return typeof child === 'string' ? [[path, child] as [string, string]] : entries(child, path)
  })
}
/** Public UI catalogs are read at runtime; canonical commands/data are never rewritten. */
export function catalogDisplayText(i18n: i18n, text: string, locale: string): string | undefined {
  const root = rootInstance(i18n)
  let locales = catalogs.get(root)
  if (!locales) {
    locales = new Map()
    catalogs.set(root, locales)
  }
  if (!watched.has(root)) {
    watched.add(root)
    // Idioma carregado sob demanda muda o conteúdo: o catálogo é refeito na próxima leitura.
    const invalidate = () => catalogs.delete(root)
    root.store?.on?.('added', invalidate)
    root.store?.on?.('removed', invalidate)
  }
  let catalog = locales.get(locale)
  if (!catalog) {
    catalog = new Map()
    for (const language of ['en-US', 'pt-BR', 'es-419'])
      fillCatalog(catalog, i18n, language, locale)
    locales.set(locale, catalog)
  }
  return catalog.get(text)
}

function fillCatalog(catalog: Map<string, string>, i18n: i18n, language: string, locale: string) {
  for (const [key, value] of entries(i18n.getResourceBundle(language, 'translation'))) {
    const translated = i18n.getResource(locale, 'translation', key) as unknown
    if (!value.includes('{{') && typeof translated === 'string') {
      catalog.set(value, translated)
      if (/^webSearch\.plan\.band\.[^.]+$/.test(key))
        catalog.set(key.slice(key.lastIndexOf('.') + 1), translated)
    }
  }
}
